import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { VendorBankAccount } from '../../models/entities/vendors/vendor-bank-account';

export interface CreateVendorBankAccountRequest {
    accountHolderName: string;
    routingNumber: string;
    accountNumber: string;
    accountType: string;
}

export interface UpdateVendorBankAccountRequest {
    accountHolderName?: string;
    routingNumber?: string;
    accountNumber?: string;
    accountType?: string;
}

@Injectable({
    providedIn: 'root',
})
export class VendorBankAccountsService {
    constructor(private _http: ApplicationHttpClient) {}

    public list(organizationId: number, vendorId: number): Observable<VendorBankAccount[]> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.VendorBankAccounts(organizationId, vendorId),
        );
        // The index endpoint answers with a resource COLLECTION, so the rows arrive under `data`
        // (`VendorBankAccountsController::index` -> `VendorBankAccountResource::collection`). Mapping
        // over the envelope itself threw `items.map is not a function`; create/update return a bare
        // resource, which is why they need no unwrapping.
        return this._http.get<{ data: any[] } | any[]>(url).pipe(
            map((response) => {
                const items = Array.isArray(response) ? response : (response?.data ?? []);
                return items.map((item) => new VendorBankAccount(item));
            }),
        );
    }

    public create(
        organizationId: number,
        vendorId: number,
        request: CreateVendorBankAccountRequest,
    ): Observable<VendorBankAccount> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.VendorBankAccounts(organizationId, vendorId),
        );
        return this._http.post<any>(url, JSON.stringify(request)).pipe(
            map((data) => new VendorBankAccount(data)),
        );
    }

    public update(
        organizationId: number,
        vendorId: number,
        bankAccountId: number,
        request: UpdateVendorBankAccountRequest,
    ): Observable<VendorBankAccount> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.VendorBankAccount(organizationId, vendorId, bankAccountId),
        );
        return this._http.put<any>(url, JSON.stringify(request)).pipe(
            map((data) => new VendorBankAccount(data)),
        );
    }

    public delete(
        organizationId: number,
        vendorId: number,
        bankAccountId: number,
    ): Observable<void> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.TreasuryModule.VendorBankAccount(organizationId, vendorId, bankAccountId),
        );
        return this._http.delete<void>(url);
    }
}
